"use client";

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { CAMPAIGN_CATEGORIES, CategoryId } from '@/lib/categories';

interface CategoryNavigationProps {
  selectedCategory?: CategoryId | null;
  showAll?: boolean;
  className?: string;
}

export function CategoryNavigation({ 
  selectedCategory, 
  showAll = true, 
  className = '' 
}: CategoryNavigationProps) {
  const _pathname = usePathname();

  return (
    <div className={`${className}`}>
      <div className="flex flex-wrap gap-2">
        {showAll && (
          <Link
            href="/browse"
            className={`
              inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors
              ${!selectedCategory 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }
            `}
          >
            <span className="mr-2">🔍</span>
            All Categories
          </Link>
        )}
        
        {CAMPAIGN_CATEGORIES.map((category) => {
          const isSelected = selectedCategory === category.id;
          
          return (
            <Link
              key={category.id}
              href={`/browse/category/${category.id}`}
              className={`
                inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors
                ${isSelected 
                  ? 'text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
              style={isSelected ? { backgroundColor: category.color } : {}}
            >
              <span className="mr-2">{category.icon}</span>
              {category.name}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

interface CategoryBreadcrumbProps {
  category?: CategoryId;
  className?: string;
}

export function CategoryBreadcrumb({ category, className = '' }: CategoryBreadcrumbProps) {
  if (!category) return null;

  const categoryData = CAMPAIGN_CATEGORIES.find(c => c.id === category);
  if (!categoryData) return null;

  return (
    <div className={`flex items-center space-x-2 text-sm text-gray-600 ${className}`}>
      <Link href="/browse" className="hover:text-blue-600">
        Browse
      </Link>
      <span>/</span>
      <span className="flex items-center">
        <span className="mr-1">{categoryData.icon}</span>
        {categoryData.name}
      </span>
    </div>
  );
}

interface CategoryHeaderProps {
  category: CategoryId;
  campaignCount?: number;
  className?: string;
}

export function CategoryHeader({ category, campaignCount, className = '' }: CategoryHeaderProps) {
  const categoryData = CAMPAIGN_CATEGORIES.find(c => c.id === category);
  if (!categoryData) return null;

  return (
    <div className={`text-center py-12 ${className}`}>
      <div className="text-6xl mb-4">{categoryData.icon}</div>
      <h1 className="text-4xl font-bold text-gray-900 mb-4">
        {categoryData.name}
      </h1>
      <p className="text-xl text-gray-600 mb-2 max-w-2xl mx-auto">
        {categoryData.description}
      </p>
      {campaignCount !== undefined && (
        <p className="text-sm text-gray-500">
          {campaignCount} {campaignCount === 1 ? 'campaign' : 'campaigns'} available
        </p>
      )}
    </div>
  );
}
